import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Link } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'
import { ASSET_KINDS, IMPORT_FIELDS, type AssetImportCommitV1, type AssetKind, type ImportField } from '@gk/contracts'
import { useEngineState, useClub } from '../club/ClubProvider.tsx'
import { kindValues, suggestMapping, type Mapping } from '../import/mapping.ts'
import { prepareImport } from '../import/prepareImport.ts'
import { readTableFile, type ParsedTable } from '../import/readTable.ts'
import type { ImportBatchView, ImportListItem, ImportRowView } from '../sync/transport.ts'
import { BackLink, Banner, errorText, Field, Pill } from '../ui/components.tsx'
import { useAction, useWriteAccess } from '../ui/hooks.ts'

type Phase = 'file' | 'map' | 'staging' | 'review' | 'committing' | 'done' | 'failed'
interface Decision { decision?: 'create' | 'skip'; tag?: string; kind?: AssetKind }

const BLOCKING = new Set(['TAG_EXISTS', 'SERIAL_DUPLICATE', 'TAG_DUP_IN_FILE', 'SERIAL_DUP_IN_FILE', 'TAG_INVALID', 'TAG_MISSING', 'KIND_UNKNOWN'])
const NEEDS_TAG = new Set(['TAG_EXISTS', 'TAG_DUP_IN_FILE', 'TAG_INVALID', 'TAG_MISSING'])
const DUPLICATES = new Set(['TAG_EXISTS', 'SERIAL_DUPLICATE', 'TAG_DUP_IN_FILE', 'SERIAL_DUP_IN_FILE'])
const TAG_RE = /^[A-Za-z0-9][A-Za-z0-9-]{1,31}$/
const STEP_OF: Record<Phase, 0 | 1 | 2 | 3> = { file: 0, map: 1, staging: 1, review: 2, committing: 2, failed: 2, done: 3 }
const FAILED = ['rejected', 'conflict', 'error']
const DONE = ['applied', 'applied_with_exception']

export function ImportPage() {
  const { t } = useTranslation()
  const { db, send, transport, org } = useClub()
  const engine = useEngineState()
  const access = useWriteAccess('asset.import')
  const { busy, error, run, clearError } = useAction()

  const [phase, setPhase] = useState<Phase>('file')
  const [file, setFile] = useState<{ name: string; sha256: string; table: ParsedTable } | null>(null)
  const [mapping, setMapping] = useState<Mapping>({})
  const [kindMap, setKindMap] = useState<Record<string, AssetKind | ''>>({})
  const [defaultKind, setDefaultKind] = useState<AssetKind | ''>('')
  const [generate, setGenerate] = useState(false)
  const [prefix, setPrefix] = useState('EQ-')
  const [batchId, setBatchId] = useState<string | null>(null)
  const [stageIds, setStageIds] = useState<string[]>([])
  const [commitId, setCommitId] = useState<string | null>(null)
  const [batch, setBatch] = useState<ImportBatchView | null>(null)
  const [rows, setRows] = useState<ImportRowView[]>([])
  const [decisions, setDecisions] = useState<Record<number, Decision>>({})
  const [failure, setFailure] = useState<string | null>(null)
  const [onlyFlagged, setOnlyFlagged] = useState(true)
  const [history, setHistory] = useState<ImportListItem[]>([])
  const [shown, setShown] = useState(100)

  const files = useRef<HTMLInputElement>(null)
  // the club on screen only (M23-0, docs/M23.md item 31): a person of two clubs used to see the other club's batches here
  useEffect(() => { void transport.importList(org.org_id).then(setHistory).catch(() => setHistory([])) }, [transport, org.org_id, phase])

  // ---- 1. file ------------------------------------------------------------------------------------------------------------
  const onFile = (f: File | undefined) => {
    if (!f) return
    clearError()
    void run(async () => {
      let r
      try { r = await readTableFile(f) } catch { throw new Error(t('import.unreadable')) }
      if (r.table.rows.length === 0) throw new Error(t('import.empty'))
      const m = suggestMapping(r.table.headers)
      setFile({ name: f.name, sha256: r.sha256, table: r.table })
      setMapping(m)
      const km: Record<string, AssetKind | ''> = {}
      for (const v of kindValues(r.table.rows, r.table.headers, m.kind)) km[v.value] = v.suggested ?? ''
      setKindMap(km)
      setGenerate(!m.readable_tag)
      setPhase('map')
    })
  }

  // ---- 2. columns ---------------------------------------------------------------------------------------------------------
  const setField = (field: ImportField, header: string) => {
    setMapping((m) => { const n = { ...m }; if (header) n[field] = header; else delete n[field]; return n })
    if (field === 'kind' && file) {
      const km: Record<string, AssetKind | ''> = {}
      for (const v of kindValues(file.table.rows, file.table.headers, header || undefined)) km[v.value] = v.suggested ?? ''
      setKindMap(km)
    }
    if (field === 'readable_tag') setGenerate(!header)
  }
  const distinctKinds = useMemo(() => (file ? kindValues(file.table.rows, file.table.headers, mapping.kind) : []), [file, mapping.kind])
  const tagOk = !!mapping.readable_tag || (generate && /^[A-Za-z0-9-]{0,20}$/.test(prefix))
  const kindOk = !!mapping.kind || !!defaultKind
  const online = engine.online !== false

  function prepare() {
    if (!file) return
    clearError()
    void run(async () => {
      // the tags already in use: the mirror plus what this device has not sent yet (read only when tags will be generated)
      const existingTags = mapping.readable_tag ? [] : [...(await db.asset.toArray()), ...(await db.overlay.where('table').equals('fleet.asset').toArray()).map((o) => o.row)].map((a) => String((a as { readable_tag: string }).readable_tag))
      // the same preparation the operator's Node path uses (import/prepareImport.ts, docs/STACK.md 14.3)
      const { payloads } = prepareImport({ table: file.table, mapping, kindMap, defaultKind, prefix, existingTags, orgId: org.org_id, fileName: file.name, sha256: file.sha256 })
      const ids: string[] = []
      for (const p of payloads) ids.push((await send('asset.import_stage@1', p)).commandId)
      setBatchId(payloads[0]!.batch_id); setStageIds(ids); setFailure(null); setPhase('staging')
    })
  }

  // ---- 3. staging / committing: watch our commands, then read the server's verdict ------------------------------------------
  const loadReview = useCallback(async (id: string) => {
    const all: ImportRowView[] = []
    let head: ImportBatchView | null = null
    for (let after = 0, guard = 0; guard < 20; guard++) {
      const v = await transport.importGet(org.org_id, id, after, 500)
      head = v.batch
      all.push(...v.rows)
      if (v.rows.length < 500) break
      after = v.rows[v.rows.length - 1]!.row_no
    }
    setBatch(head); setRows(all); setBatchId(id)
    // M23 item 37: a clean file shows every row (so a swapped column is seen before anything is created); a file with flagged rows shows those first
    setOnlyFlagged(all.some((r) => r.flags.some((f) => BLOCKING.has(f))))
    return head
  }, [transport, org.org_id])

  useEffect(() => {
    if ((phase !== 'staging' && phase !== 'committing') || !batchId) return
    let stopped = false
    let timer: ReturnType<typeof setTimeout> | undefined
    const ids = phase === 'staging' ? stageIds : commitId ? [commitId] : []
    const tick = async () => {
      if (stopped) return
      const [out, arch] = await Promise.all([db.outbox.bulkGet(ids), db.archive.bulkGet(ids)])
      const bad = out.find((r) => r && FAILED.includes(r.status))
      if (bad) { setFailure(bad.server_code ?? null); setPhase('failed'); return }
      const settled = ids.every((id, i) => (out[i] && DONE.includes(out[i]!.status)) || arch[i])
      if (settled) {
        try {
          const v = await transport.importGet(org.org_id, batchId, 0, 1)
          if (phase === 'staging' && v.batch?.status === 'staged') { await loadReview(batchId); if (!stopped) setPhase('review'); return }
          if (phase === 'committing' && v.batch?.status === 'committed') { await loadReview(batchId); if (!stopped) setPhase('done'); return }
        } catch { /* offline or busy: keep waiting */ }
      }
      timer = setTimeout(() => void tick(), 1000)
    }
    void tick()
    return () => { stopped = true; clearTimeout(timer) }
  }, [phase, batchId, stageIds, commitId, db, transport, org.org_id, loadReview])

  // ---- 4. review ----------------------------------------------------------------------------------------------------------
  const flagged = useMemo(() => rows.filter((r) => r.flags.some((f) => BLOCKING.has(f))), [rows])
  const warned = useMemo(() => rows.filter((r) => r.flags.length > 0 && !r.flags.some((f) => BLOCKING.has(f))), [rows])
  const usedTags = useMemo(() => {
    const m = new Map<string, number>()
    for (const r of rows) {
      const d = decisions[r.row_no]
      const tag = d?.decision === 'create' && d.tag ? d.tag.toUpperCase() : r.flags.some((f) => NEEDS_TAG.has(f)) ? null : (r.normalised.readable_tag as string | null)
      if (tag && d?.decision !== 'skip') m.set(tag, (m.get(tag) ?? 0) + 1)
    }
    return m
  }, [rows, decisions])

  const problem = (r: ImportRowView): string | null => {
    const d = decisions[r.row_no]
    if (!d?.decision) return 'decide'
    if (d.decision === 'skip') return null
    if (r.flags.some((f) => NEEDS_TAG.has(f))) {
      if (!d.tag || !TAG_RE.test(d.tag)) return 'tag'
      if ((usedTags.get(d.tag.toUpperCase()) ?? 0) > 1) return 'tag'
    }
    if (r.flags.includes('KIND_UNKNOWN') && !d.kind) return 'kind'
    return null
  }
  const open = flagged.filter((r) => problem(r) !== null)
  const toCreate = rows.length - flagged.filter((r) => decisions[r.row_no]?.decision === 'skip').length
  const patch = (n: number, p: Decision) => setDecisions((d) => ({ ...d, [n]: { ...d[n], ...p } }))
  const skipDuplicates = () => setDecisions((d) => { const n = { ...d }; for (const r of flagged) if (r.flags.some((f) => DUPLICATES.has(f)) && !r.flags.some((f) => f === 'TAG_INVALID' || f === 'TAG_MISSING' || f === 'KIND_UNKNOWN')) n[r.row_no] = { ...n[r.row_no], decision: 'skip' }; return n })

  function commit() {
    if (!batchId) return
    clearError()
    void run(async () => {
      const list: NonNullable<AssetImportCommitV1['decisions']> = flagged.map((r) => {
        const d = decisions[r.row_no]!
        return { row_no: r.row_no, decision: d.decision!, ...(d.decision === 'create' && d.tag && r.flags.some((f) => NEEDS_TAG.has(f)) ? { readable_tag: d.tag } : {}), ...(d.decision === 'create' && d.kind && r.flags.includes('KIND_UNKNOWN') ? { kind: d.kind } : {}) }
      })
      const res = await send('asset.import_commit@2', { batch_id: batchId, ...(list.length ? { decisions: list } : {}) })
      setCommitId(res.commandId); setFailure(null); setPhase('committing')
    })
  }

  const reset = () => { setPhase('file'); setFile(null); setBatch(null); setRows([]); setDecisions({}); setStageIds([]); setCommitId(null); setFailure(null); setBatchId(null); clearError() }
  const resume = (item: ImportListItem) => void run(async () => {
    const head = await loadReview(item.id)
    setDecisions({})
    setPhase(head?.status === 'committed' ? 'done' : head?.status === 'staged' ? 'review' : 'file')
  })

  if (!access.permitted) return <section className="flex flex-col gap-3"><Banner tone="warn">{t('import.notAllowed')}</Banner><Link to="/assets" className="btn">{t('common.back')}</Link></section>
  const steps = ['file', 'map', 'review', 'done'] as const
  const flagText = (r: ImportRowView, f: string) => t(`import.flag.${f}`, { tag: r.duplicate_of_tag ?? '', row: r.duplicate_in_file_row ?? '' })

  return (
    <section className="flex flex-col gap-4" data-testid="import-page">
      <BackLink to="/assets">← {t('common.back')}</BackLink>
      <h1 className="text-2xl font-bold">{t('import.title')}</h1>
      <ol className="flex gap-2 text-sm font-semibold" aria-label="steps">
        {steps.map((s, i) => <li key={s} className={`flex-1 rounded-full px-2 py-1 text-center ${STEP_OF[phase] >= i ? 'bg-emerald-700 text-white' : 'bg-slate-200 text-slate-700 dark:bg-night-line dark:text-slate-200'}`}>{t(`import.steps.${s}`)}</li>)}
      </ol>
      {access.readOnlyMode && <Banner tone="warn">{t('platform.readOnly')}</Banner>}
      {error && <Banner tone="error">{error}</Banner>}

      {phase === 'file' && (
        <>
          <p className="text-sm opacity-80">{t('import.intro')}</p>
          <input ref={files} type="file" accept=".csv,.xlsx,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" className="sr-only" data-testid="import-file" onChange={(e) => { onFile(e.target.files?.[0]); e.target.value = '' }} />
          <button className="btn btn-primary" disabled={busy || access.readOnlyMode} onClick={() => files.current?.click()} data-testid="import-choose">{t('import.choose')}</button>
          <h2 className="mt-2 text-lg font-bold">{t('import.historyTitle')}</h2>
          {history.length === 0 ? <p className="opacity-70">{t('import.historyNone')}</p> : (
            <ul className="flex flex-col gap-2" data-testid="import-history">
              {history.map((h) => (
                <li key={h.id} className="card flex items-center justify-between gap-3" data-testid="import-history-row">
                  <div className="min-w-0"><p className="truncate font-semibold">{h.file_name}</p><p className="meta">{h.rows_received}/{h.total_rows}{h.created_asset_count != null ? ` · ${h.created_asset_count}` : ''}</p></div>
                  <div className="flex items-center gap-2">
                    <Pill tone={h.status === 'committed' ? 'green' : 'amber'}>{t(`import.status.${h.status}`)}</Pill>
                    {h.status !== 'staging' && <button className="btn !min-h-11 !px-3" onClick={() => resume(h)}>{h.status === 'staged' ? t('import.resume') : t('common.open')}</button>}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </>
      )}

      {phase === 'map' && file && (
        <>
          <p className="text-sm font-semibold">{file.name} · {t('import.rowCount', { count: file.table.rows.length })} · {t('import.colCount', { count: file.table.headers.length })}</p>
          {file.table.warnings.includes('too_many_rows') && <Banner tone="warn">{t('import.tooMany')}</Banner>}
          {file.table.encoding === 'windows-1252' && <Banner tone="info">{t('import.encodingNote')}</Banner>}
          <h2 className="text-lg font-bold">{t('import.mapTitle')}</h2>
          <p className="text-sm opacity-75">{t('import.mapHelp')}</p>
          <div className="flex flex-col gap-3" data-testid="import-mapping">
            {IMPORT_FIELDS.map((field) => (
              <Field key={field} label={t(`import.field.${field}`)} htmlFor={`map-${field}`}>
                <select id={`map-${field}`} className="input" value={mapping[field] ?? ''} onChange={(e) => setField(field, e.target.value)}>
                  <option value="">{t('import.ignore')}</option>
                  {file.table.headers.map((h) => <option key={h} value={h}>{h}</option>)}
                </select>
              </Field>
            ))}
          </div>
          {!mapping.readable_tag && (
            <div className="card flex flex-col gap-3">
              <label className="flex min-h-12 items-center gap-3 font-medium"><input type="checkbox" className="size-6" checked={generate} onChange={(e) => setGenerate(e.target.checked)} />{t('import.generateTags')}</label>
              {generate && <Field label={t('import.prefix')} hint={t('import.prefixHelp')} htmlFor="prefix"><input id="prefix" className="input uppercase" maxLength={20} value={prefix} onChange={(e) => setPrefix(e.target.value)} /></Field>}
            </div>
          )}
          <div className="card flex flex-col gap-3">
            <h3 className="font-bold">{t('import.kindsTitle')}</h3>
            {mapping.kind && distinctKinds.length > 0 && <p className="text-sm opacity-75">{t('import.kindsHelp')}</p>}
            {mapping.kind && distinctKinds.map((v) => (
              <div key={v.value} className="grid grid-cols-2 items-center gap-3">
                <span className="truncate font-medium">{v.value} <span className="meta">×{v.count}</span></span>
                <select className="input !min-h-11" aria-label={v.value} value={kindMap[v.value] ?? ''} onChange={(e) => setKindMap((m) => ({ ...m, [v.value]: e.target.value as AssetKind | '' }))}>
                  <option value="">{t('import.kindUnknown')}</option>
                  {ASSET_KINDS.map((k) => <option key={k} value={k}>{t(`assets.kinds.${k}`)}</option>)}
                </select>
              </div>
            ))}
            <Field label={t('import.defaultKind')} htmlFor="default-kind">
              <select id="default-kind" className="input" value={defaultKind} onChange={(e) => setDefaultKind(e.target.value as AssetKind | '')}>
                <option value="">{t('import.chooseKind')}</option>
                {ASSET_KINDS.map((k) => <option key={k} value={k}>{t(`assets.kinds.${k}`)}</option>)}
              </select>
            </Field>
          </div>
          {mapping.current_hours && <Banner tone="info">{t('import.hoursNote')}</Banner>}
          <h3 className="font-bold">{t('import.sample')}</h3>
          <div className="overflow-x-auto rounded-xl border-2 border-slate-300">
            <table className="w-full text-left text-sm">
              <thead><tr>{file.table.headers.map((h) => <th key={h} className="whitespace-nowrap px-2 py-1">{h}</th>)}</tr></thead>
              <tbody>{file.table.rows.slice(0, 5).map((r, i) => <tr key={i} className="border-t">{r.map((c, j) => <td key={j} className="whitespace-nowrap px-2 py-1">{c}</td>)}</tr>)}</tbody>
            </table>
          </div>
          {!tagOk && <Banner tone="warn">{t('import.needTag')}</Banner>}
          {tagOk && !kindOk && <Banner tone="warn">{t('import.needKind')}</Banner>}
          <div className="grid grid-cols-2 gap-3">
            <button className="btn" onClick={reset}>{t('common.cancel')}</button>
            <button className="btn btn-primary" disabled={!tagOk || !kindOk || busy || access.readOnlyMode} onClick={prepare} data-testid="import-prepare">{t('import.prepare')}</button>
          </div>
        </>
      )}

      {(phase === 'staging' || phase === 'committing') && (
        <div className="flex flex-col gap-3" data-testid="import-working">
          <Banner tone="info">{phase === 'staging' ? (online ? t('import.checking') : t('import.waitingNetwork')) : (online ? t('import.committing') : t('import.waitingNetwork'))}</Banner>
        </div>
      )}

      {phase === 'failed' && (
        <div className="flex flex-col gap-3" data-testid="import-failed">
          <Banner tone="error">{(commitId ? t('import.commitRejected', { reason: errorText(t, failure) }) : t('import.stageRejected', { reason: errorText(t, failure) }))}</Banner>
          {failure === 'IMPORT_FILE_ALREADY_IMPORTED' && batchId && <button className="btn" onClick={() => resume({ id: batchId } as ImportListItem)}>{t('common.open')}</button>}
          <button className="btn" onClick={() => { if (commitId && batchId) { setCommitId(null); void loadReview(batchId).then(() => setPhase('review')) } else reset() }}>{commitId ? t('common.back') : t('import.retry')}</button>
        </div>
      )}

      {phase === 'review' && batch && (
        <>
          <h2 className="text-lg font-bold">{t('import.reviewTitle')} · {batch.file_name}</h2>
          <p className="font-semibold" data-testid="import-summary">{flagged.length === 0 ? t('import.allClear') : `${t('import.readyCount', { count: rows.length - flagged.length })} · ${t('import.flaggedCount', { count: flagged.length })}`}</p>
          {batch.duplicate_content_of && <Banner tone="warn">{t('import.sameContent', { file: history.find((h) => h.id === batch.duplicate_content_of)?.file_name ?? '…' })}</Banner>}
          {warned.length > 0 && <Banner tone="info">{[...new Set(warned.flatMap((r) => r.flags))].map((f) => t(`import.flag.${f}`, { tag: '', row: '' })).join(' · ')}</Banner>}
          <div className="flex flex-wrap gap-2">
            {flagged.length > 0 && <button className="btn !min-h-11" onClick={skipDuplicates} data-testid="skip-duplicates">{t('import.skipDuplicates')}</button>}
            <button className="btn !min-h-11" onClick={() => setOnlyFlagged((v) => !v)} data-testid="import-toggle-rows">{onlyFlagged ? t('import.showAll') : t('import.showFlagged')}</button>
          </div>
          <ul className="flex flex-col gap-3" data-testid="import-rows">
            {(onlyFlagged ? flagged : rows).slice(0, shown).map((r) => {
              const isFlagged = r.flags.some((f) => BLOCKING.has(f))
              const d = decisions[r.row_no] ?? {}
              const p = isFlagged ? problem(r) : null
              return (
                <li key={r.row_no} className={`card flex flex-col gap-2 ${p ? 'border-amber-500' : ''}`} data-testid="import-row">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <p className="font-bold">{r.normalised.readable_tag ?? '—'} <span className="meta font-normal">{t('import.row', { n: r.row_no })}</span></p>
                      <p className="meta truncate" data-testid="import-row-facts">{[r.normalised.make, r.normalised.model, r.normalised.serial_no].filter(Boolean).join(' · ')}</p>
                    </div>
                    {!isFlagged && <Pill tone="green">OK</Pill>}
                  </div>
                  {r.flags.length > 0 && <ul className="flex flex-wrap gap-1">{r.flags.map((f) => <li key={f}><Pill tone={BLOCKING.has(f) ? 'amber' : 'gray'}>{flagText(r, f)}</Pill></li>)}</ul>}
                  {isFlagged && (
                    <div className="flex flex-col gap-2">
                      <div className="grid grid-cols-2 gap-2" role="radiogroup" aria-label={t('import.decide')}>
                        {(['create', 'skip'] as const).map((opt) => (
                          <button key={opt} role="radio" aria-checked={d.decision === opt} className={`btn !min-h-11 ${d.decision === opt ? 'btn-primary' : ''}`} onClick={() => patch(r.row_no, { decision: opt })} data-testid={`row-${r.row_no}-${opt}`}>{t(`import.${opt}`)}</button>
                        ))}
                      </div>
                      {d.decision === 'create' && r.flags.some((f) => NEEDS_TAG.has(f)) && (
                        <Field label={t('import.fixTag')} error={p === 'tag' ? t('assets.tagHelp') : null} htmlFor={`tag-${r.row_no}`}>
                          <input id={`tag-${r.row_no}`} className="input uppercase" maxLength={32} value={d.tag ?? ''} onChange={(e) => patch(r.row_no, { tag: e.target.value.trim().toUpperCase() })} data-testid={`row-${r.row_no}-tag`} />
                        </Field>
                      )}
                      {d.decision === 'create' && r.flags.includes('KIND_UNKNOWN') && (
                        <Field label={t('import.fixKind')} htmlFor={`kind-${r.row_no}`}>
                          <select id={`kind-${r.row_no}`} className="input" value={d.kind ?? ''} onChange={(e) => patch(r.row_no, { kind: (e.target.value || undefined) as AssetKind | undefined })}>
                            <option value="">{t('import.chooseKind')}</option>
                            {ASSET_KINDS.map((k) => <option key={k} value={k}>{t(`assets.kinds.${k}`)}</option>)}
                          </select>
                        </Field>
                      )}
                    </div>
                  )}
                </li>
              )
            })}
          </ul>
          {(onlyFlagged ? flagged : rows).length > shown && <button className="btn" onClick={() => setShown((n) => n + 100)}>{t('common.next')}</button>}
          {open.length > 0 && <Banner tone="warn">{t('import.needDecisions')} ({open.length})</Banner>}
          <div className="grid grid-cols-2 gap-3">
            <button className="btn" onClick={reset}>{t('common.cancel')}</button>
            <button className="btn btn-primary" disabled={open.length > 0 || busy || access.readOnlyMode} onClick={commit} data-testid="import-commit">{t('import.commit', { count: toCreate })}</button>
          </div>
        </>
      )}

      {phase === 'done' && batch && (
        <div className="flex flex-col gap-3" data-testid="import-done">
          <Banner tone="ok">{`${t('import.createdCount', { count: batch.created_asset_count ?? 0 })} · ${t('import.skippedCount', { count: batch.total_rows - (batch.created_asset_count ?? 0) })}`}</Banner>
          <Link to="/assets" className="btn btn-primary">{t('import.seeAssets')}</Link>
          <button className="btn" onClick={reset}>{t('import.again')}</button>
        </div>
      )}
    </section>
  )
}

